
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Medication, MedicationLog, Language } from '../types';
import { t } from '../locales';
import { X, Pill, Plus, Calendar, AlertCircle, CheckCircle2, History, Trash2, Box, Camera } from 'lucide-react';
import BarcodeScannerModal from './BarcodeScannerModal';

interface PharmacyModalProps {
  isOpen: boolean;
  onClose: () => void;
  medications: Medication[];
  medicationLogs: MedicationLog[];
  onAddMedication: (med: Medication) => void;
  onDeleteMedication: (id: string) => void;
  onTakeMedication: (medId: string, amount: number) => void;
  language: Language;
}

const PharmacyModal: React.FC<PharmacyModalProps> = ({
    isOpen, onClose, medications, medicationLogs, onAddMedication, onDeleteMedication, onTakeMedication, language
}) => {
  const [activeTab, setActiveTab] = useState<'cabinet' | 'add'>('cabinet');
  const [showScanner, setShowScanner] = useState(false);
  const [newMedName, setNewMedName] = useState('');
  const [newMedDosage, setNewMedDosage] = useState('');
  const [newMedStock, setNewMedStock] = useState('30');
  const [newMedExpiry, setNewMedExpiry] = useState('');
  const [newMedBarcode, setNewMedBarcode] = useState('');
  const [takenFeedback, setTakenFeedback] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleBarcodeDetected = (code: string) => {
      setNewMedBarcode(code);
      setShowScanner(false);
      setActiveTab('add');
      // In a real app, we would fetch drug data here. For now, we simulate a fill or let user enter.
      // alert(`Barcode ${code} scanned. Please enter details.`); 
  };

  const handleAdd = () => {
      if (!newMedName) return;
      const newMed: Medication = {
          id: crypto.randomUUID(),
          name: newMedName,
          dosage: newMedDosage,
          stock: parseInt(newMedStock) || 0,
          unit: 'pills', // simplified for v0.2
          expirationDate: newMedExpiry ? new Date(newMedExpiry) : undefined,
          barcode: newMedBarcode,
          addedAt: new Date()
      };
      onAddMedication(newMed);
      setNewMedName('');
      setNewMedDosage('');
      setNewMedStock('30');
      setNewMedExpiry('');
      setNewMedBarcode('');
      setActiveTab('cabinet');
  };

  const handleTake = (medId: string) => {
      onTakeMedication(medId, 1);
      setTakenFeedback(medId);
      setTimeout(() => setTakenFeedback(null), 1500);
  };

  // Sort: Expired first, then expiring soon, then healthy
  const sortedMeds = useMemo(() => {
      return [...medications].sort((a, b) => {
          const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : 9999999999999;
          const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : 9999999999999;
          return dateA - dateB;
      });
  }, [medications]);

  const getExpiryStatus = (dateInput?: Date | string) => {
      if (!dateInput) return { color: 'text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800', label: 'No date', icon: null };
      
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return { color: 'text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800', label: 'Invalid date', icon: null };

      const now = new Date();
      const diffTime = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return { color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', label: t(language, 'expired'), icon: <AlertCircle size={12}/> };
      if (diffDays < 30) return { color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', label: `${t(language, 'expiringSoon')} (${diffDays}d)`, icon: <AlertCircle size={12}/> };
      return { color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: `${t(language, 'goodCondition')} (${diffDays} ${t(language, 'daysLeft')})`, icon: <CheckCircle2 size={12}/> };
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-slideIn">
      <div className="w-full h-full max-w-md mx-auto flex flex-col bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-violet-50/50 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 pt-safe">
            <h2 className="text-xl font-bold text-violet-700 dark:text-violet-400 flex items-center gap-2">
                <Pill className="h-6 w-6" />
                {t(language, 'pharmacy')}
            </h2>
            <button onClick={onClose} className="p-2 rounded-full bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm border border-gray-100 dark:border-gray-700">
                <X size={24} />
            </button>
        </div>

        {/* Tabs */}
        <div className="flex p-4 gap-4 bg-violet-50/30 dark:bg-gray-900/50">
            <button onClick={() => setActiveTab('cabinet')} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'cabinet' ? 'bg-violet-500 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Box size={16} />
                {t(language, 'myCabinet')}
            </button>
            <button onClick={() => setActiveTab('add')} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === 'add' ? 'bg-violet-500 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700'}`}>
                <Plus size={16} />
                {t(language, 'addMedication')}
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-20 min-h-0 bg-violet-50/10 dark:bg-gray-900">
            
            {activeTab === 'cabinet' && (
                <div className="space-y-3 pt-4">
                    {sortedMeds.length === 0 ? (
                        <div className="text-center text-gray-400 py-10 flex flex-col items-center gap-3">
                            <Box size={48} strokeWidth={1} />
                            <p>{t(language, 'medsEmpty')}</p>
                        </div>
                    ) : (
                        sortedMeds.map(med => {
                            const status = getExpiryStatus(med.expirationDate);
                            return (
                                <div key={med.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col gap-2">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-bold text-gray-800 dark:text-white text-base">{med.name}</h3>
                                            <p className="text-xs text-gray-500">{med.dosage} • {t(language, 'stock')}: {med.stock}</p>
                                        </div>
                                        <button 
                                            onClick={() => handleTake(med.id)}
                                            disabled={med.stock <= 0}
                                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                                                takenFeedback === med.id 
                                                ? 'bg-green-500 text-white' 
                                                : med.stock > 0 ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            }`}
                                        >
                                            {takenFeedback === med.id ? t(language, 'medicationLogged') : t(language, 'takeDose')}
                                        </button>
                                    </div>
                                    
                                    <div className="flex justify-between items-center pt-2 border-t border-gray-50 dark:border-gray-700/50 mt-1">
                                        <div className={`flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-md ${status.bg} ${status.color}`}>
                                            {status.icon}
                                            {status.label}
                                        </div>
                                        <button onClick={() => onDeleteMedication(med.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {activeTab === 'add' && (
                <div className="space-y-5 pt-6">
                    <button 
                        onClick={() => setShowScanner(true)}
                        className="w-full py-4 bg-gray-800 dark:bg-gray-700 text-white rounded-xl flex items-center justify-center gap-2 font-bold shadow-lg active:scale-95 transition-transform"
                    >
                        <Camera size={20} />
                        {t(language, 'scanDrug')}
                    </button>
                    
                    <div className="relative flex items-center justify-center">
                        <div className="border-t border-gray-200 w-full"></div>
                        <span className="bg-gray-50 dark:bg-gray-900 px-3 text-xs text-gray-400 font-medium absolute">OR MANUAL</span>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'medicationName')}</label>
                            <input 
                                type="text" 
                                value={newMedName}
                                onChange={(e) => setNewMedName(e.target.value)}
                                className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-violet-500"
                                placeholder="e.g. Ibuprofen"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'dosage')}</label>
                                <input 
                                    type="text" 
                                    value={newMedDosage}
                                    onChange={(e) => setNewMedDosage(e.target.value)}
                                    className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-violet-500"
                                    placeholder="e.g. 200mg"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'stock')}</label>
                                <input 
                                    type="number" 
                                    value={newMedStock}
                                    onChange={(e) => setNewMedStock(e.target.value)}
                                    className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-violet-500 font-bold"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'expiryDate')}</label>
                            <input 
                                type="date" 
                                value={newMedExpiry}
                                onChange={(e) => setNewMedExpiry(e.target.value)}
                                className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        {newMedBarcode && (
                            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded text-center">Linked Barcode: <span className="font-mono">{newMedBarcode}</span></div>
                        )}
                        <button 
                            onClick={handleAdd}
                            disabled={!newMedName}
                            className="w-full py-3 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold rounded-xl shadow-lg transition-transform active:scale-95"
                        >
                            {t(language, 'save')}
                        </button>
                    </div>
                </div>
            )}
        </div>
      </div>
      <BarcodeScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)} onDetected={handleBarcodeDetected} language={language} />
    </div>,
    document.body
  );
};

export default PharmacyModal;
